import { useEffect, useRef, useState } from "react";
import { API, api } from "../lib/api";
import { AdminGate, AdminShell } from "./AdminGate";
import { toast } from "sonner";
import { ImagePlus, RotateCcw, Save } from "lucide-react";

function SplashCard() {
  const [preview, setPreview] = useState(`${API}/public/splash?t=${Date.now()}`);
  const [temCustom, setTemCustom] = useState(true);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const refresh = () => {
    setTemCustom(true);
    setPreview(`${API}/public/splash?t=${Date.now()}`);
  };

  const enviar = async () => {
    if (!file) return toast.error("Selecione uma imagem primeiro");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/splash", fd);
      toast.success("Imagem da tela inicial atualizada!");
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao enviar a imagem");
    } finally {
      setSaving(false);
    }
  };

  const restaurar = async () => {
    setSaving(true);
    try {
      await api.delete("/admin/splash");
      toast.success("Imagem padrão restaurada!");
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      setPreview("/splash.jpg");
      setTemCustom(false);
    } catch {
      toast.error("Falha ao restaurar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-4" data-testid="splash-settings-card">
      <h3 className="text-xs font-bold tracking-wider uppercase text-slate-400">Tela inicial (Splash)</h3>
      <p className="text-[11px] text-slate-400">
        Imagem exibida por 4 segundos ao abrir o app, antes da tela principal. Formatos: JPG, PNG ou WebP (máx 10MB).
      </p>
      <div className="rounded-xl overflow-hidden border border-slate-200 bg-[#0a1f44] flex items-center justify-center max-h-72">
        <img
          data-testid="splash-preview"
          src={preview}
          alt="Splash atual"
          className="w-full max-h-72 object-contain"
          onError={() => { if (temCustom) { setTemCustom(false); setPreview("/splash.jpg"); } }}
        />
      </div>
      {!temCustom && <p className="text-[11px] text-slate-400">Exibindo a imagem padrão do app.</p>}
      <input
        ref={inputRef}
        data-testid="splash-file-input"
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="block w-full text-xs text-slate-500 file:mr-3 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:bg-gold-light file:text-navy file:text-xs file:font-bold"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
      />
      <div className="grid grid-cols-2 gap-2">
        <button
          data-testid="splash-upload-btn"
          onClick={enviar}
          disabled={saving || !file}
          className="py-3 rounded-xl bg-navy text-[#F5B301] font-heading font-extrabold text-xs flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
        >
          <ImagePlus className="w-4 h-4" /> ENVIAR IMAGEM
        </button>
        <button
          data-testid="splash-reset-btn"
          onClick={restaurar}
          disabled={saving}
          className="py-3 rounded-xl border-2 border-slate-200 text-slate-500 font-heading font-extrabold text-xs flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
        >
          <RotateCcw className="w-4 h-4" /> RESTAURAR PADRÃO
        </button>
      </div>
    </div>
  );
}

const inputCls =
  "mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#F5B301] placeholder:text-slate-400";

function SettingsForm({ user }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch(() => toast.error("Falha ao carregar"));
  }, []);

  if (!s) {
    return (
      <AdminShell user={user} title="Configurações">
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-[#F5B301] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminShell>
    );
  }

  const save = async () => {
    setSaving(true);
    let fees = s.card_fees;
    if (typeof fees === "string") {
      try {
        fees = JSON.parse(fees);
      } catch {
        setSaving(false);
        return toast.error("JSON de taxas do cartão inválido");
      }
    }
    try {
      await api.put("/admin/settings", {
        google_maps_key: s.google_maps_key,
        google_place_id: s.google_place_id,
        navigation_app: s.navigation_app,
        show_toll: s.show_toll,
        whatsapp_number: s.whatsapp_number,
        maps_url: s.maps_url,
        sinal_pct: parseFloat(s.sinal_pct) || 30,
        pix_key: s.pix_key,
        pending_ttl_minutes: parseInt(s.pending_ttl_minutes) || 60,
        percentual_empresa: parseFloat(s.percentual_empresa) || 0,
        alerta_som: s.alerta_som || "buzina",
        pedagio_pracas: (Array.isArray(s.pedagio_pracas) ? s.pedagio_pracas : [])
          .map((p) => ({
            nome: p.nome || "",
            valor: parseFloat(p.valor) || 0,
            municipios: (Array.isArray(p.municipios) ? p.municipios.join(",") : p.municipios || "")
              .split(",").map((m) => m.trim()).filter(Boolean),
          }))
          .filter((p) => p.nome && p.valor > 0 && p.municipios.length > 0),
        card_fees: fees,
        admin_emails: Array.isArray(s.admin_emails) ? s.admin_emails : String(s.admin_emails).split(",").map((e) => e.trim()).filter(Boolean),
      });
      toast.success("Configurações salvas!");
    } catch {
      toast.error("Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell user={user} title="Configurações">
      <div className="px-4 mt-4 space-y-4" data-testid="admin-settings-form">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-4">
          <h3 className="text-xs font-bold tracking-wider uppercase text-slate-400">Google Maps Platform</h3>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Chave da API Google Maps</label>
            <input
              data-testid="setting-google-key"
              className={inputCls}
              placeholder="AIza..."
              value={s.google_maps_key || ""}
              onChange={(e) => setS({ ...s, google_maps_key: e.target.value })}
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Sem chave, a rota é calculada por estimativa (OSRM). Com a chave, distância real do Google.
            </p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">ID do Perfil da Empresa (Place ID)</label>
            <input
              data-testid="setting-place-id"
              className={inputCls}
              placeholder="ChIJ..."
              value={s.google_place_id || ""}
              onChange={(e) => setS({ ...s, google_place_id: e.target.value })}
            />
            <p className="text-[11px] text-slate-400 mt-1">Necessário para exibir avaliações reais do Google no app.</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Link do perfil no Google</label>
            <input
              data-testid="setting-maps-url"
              className={inputCls}
              placeholder="https://share.google/..."
              value={s.maps_url || ""}
              onChange={(e) => setS({ ...s, maps_url: e.target.value })}
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-4">
          <h3 className="text-xs font-bold tracking-wider uppercase text-slate-400">Operação</h3>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Aplicativo de navegação do motorista</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {["google", "waze"].map((app) => (
                <button
                  key={app}
                  data-testid={`setting-nav-${app}`}
                  onClick={() => setS({ ...s, navigation_app: app })}
                  className={`py-3 rounded-xl border-2 text-sm font-semibold active:scale-95 transition-all ${
                    s.navigation_app === app ? "border-[#F5B301] bg-gold-light text-navy" : "border-slate-200 text-slate-500"
                  }`}
                >
                  {app === "google" ? "Google Maps" : "Waze"}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Mostrar pedágio ao cliente</label>
            <button
              data-testid="setting-show-toll"
              onClick={() => setS({ ...s, show_toll: !s.show_toll })}
              className={`w-12 h-7 rounded-full transition-colors relative ${s.show_toll ? "bg-[#F5B301]" : "bg-slate-200"}`}
            >
              <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${s.show_toll ? "left-6" : "left-1"}`} />
            </button>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Praças de pedágio (tabela interna)</label>
            <p className="text-[11px] text-slate-400 mt-1 mb-2">
              Se a rota passar por uma dessas cidades, o pedágio é cobrado mesmo que o Google não informe. O valor segue a regra de cobrança do sistema.
            </p>
            <div className="space-y-2" data-testid="toll-plazas-list">
              {(Array.isArray(s.pedagio_pracas) ? s.pedagio_pracas : []).map((p, i) => (
                <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2" data-testid={`toll-plaza-${i}`}>
                  <input
                    data-testid={`toll-plaza-nome-${i}`}
                    className={inputCls}
                    placeholder="Nome da praça (ex.: Ponte Rio-Niterói)"
                    value={p.nome || ""}
                    onChange={(e) => {
                      const arr = [...s.pedagio_pracas];
                      arr[i] = { ...arr[i], nome: e.target.value };
                      setS({ ...s, pedagio_pracas: arr });
                    }}
                  />
                  <div className="grid grid-cols-[1fr_110px] gap-2">
                    <input
                      data-testid={`toll-plaza-cidades-${i}`}
                      className={inputCls}
                      placeholder="Cidades (separadas por vírgula)"
                      value={Array.isArray(p.municipios) ? p.municipios.join(", ") : p.municipios || ""}
                      onChange={(e) => {
                        const arr = [...s.pedagio_pracas];
                        arr[i] = { ...arr[i], municipios: e.target.value };
                        setS({ ...s, pedagio_pracas: arr });
                      }}
                    />
                    <input
                      data-testid={`toll-plaza-valor-${i}`}
                      className={inputCls}
                      placeholder="R$"
                      type="number"
                      step="0.01"
                      min="0"
                      value={p.valor ?? ""}
                      onChange={(e) => {
                        const arr = [...s.pedagio_pracas];
                        arr[i] = { ...arr[i], valor: e.target.value };
                        setS({ ...s, pedagio_pracas: arr });
                      }}
                    />
                  </div>
                  <button
                    data-testid={`toll-plaza-remover-${i}`}
                    onClick={() => setS({ ...s, pedagio_pracas: s.pedagio_pracas.filter((_, x) => x !== i) })}
                    className="text-[11px] font-bold text-red-500"
                  >
                    Remover praça
                  </button>
                </div>
              ))}
            </div>
            <button
              data-testid="toll-plaza-add"
              onClick={() => setS({ ...s, pedagio_pracas: [...(Array.isArray(s.pedagio_pracas) ? s.pedagio_pracas : []), { nome: "", valor: "", municipios: "" }] })}
              className="mt-2 w-full py-2.5 rounded-xl border-2 border-dashed border-[#F5B301] text-navy text-xs font-bold active:scale-95 transition-transform"
            >
              + ADICIONAR PRAÇA DE PEDÁGIO
            </button>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">WhatsApp (somente números, com DDI+DDD)</label>
            <input
              data-testid="setting-whatsapp"
              className={inputCls}
              value={s.whatsapp_number || ""}
              onChange={(e) => setS({ ...s, whatsapp_number: e.target.value.replace(/\D/g, "") })}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">E-mails administradores (separados por vírgula)</label>
            <textarea
              data-testid="setting-admin-emails"
              className={`${inputCls} min-h-[70px]`}
              value={Array.isArray(s.admin_emails) ? s.admin_emails.join(", ") : s.admin_emails}
              onChange={(e) => setS({ ...s, admin_emails: e.target.value })}
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-4">
          <h3 className="text-xs font-bold tracking-wider uppercase text-slate-400">Pagamento e reserva</h3>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Percentual do sinal (%)</label>
            <input
              data-testid="setting-sinal-pct"
              type="number"
              min="1"
              max="100"
              className={inputCls}
              value={s.sinal_pct ?? 30}
              onChange={(e) => setS({ ...s, sinal_pct: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Chave Pix</label>
            <input
              data-testid="setting-pix-key"
              className={inputCls}
              value={s.pix_key || ""}
              onChange={(e) => setS({ ...s, pix_key: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Tempo de retenção da reserva pendente (minutos)</label>
            <input
              data-testid="setting-pending-ttl"
              type="number"
              min="5"
              className={inputCls}
              value={s.pending_ttl_minutes ?? 60}
              onChange={(e) => setS({ ...s, pending_ttl_minutes: e.target.value })}
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Se o Pix não for confirmado nesse período, a solicitação expira automaticamente e a data volta a ficar disponível.
            </p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Percentual da empresa (%)</label>
            <input
              data-testid="setting-percentual-empresa"
              type="number"
              min="0"
              max="100"
              className={inputCls}
              value={s.percentual_empresa ?? 0}
              onChange={(e) => setS({ ...s, percentual_empresa: e.target.value })}
            />
            <p className="text-[11px] text-slate-400 mt-1">Referência interna para gestão financeira (não interfere no valor definido por corrida).</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Som do alerta de novo frete (motorista)</label>
            <select
              data-testid="setting-alerta-som"
              className={inputCls}
              value={s.alerta_som || "buzina"}
              onChange={(e) => setS({ ...s, alerta_som: e.target.value })}
            >
              <option value="buzina">🚛 Buzina de caminhão (padrão)</option>
            </select>
            <p className="text-[11px] text-slate-400 mt-1">Estrutura preparada para novos sons sem alterar o app.</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Taxas do cartão (JSON — % por bandeira/parcela)</label>
            <textarea
              data-testid="setting-card-fees"
              className={`${inputCls} min-h-[110px] font-mono text-[11px]`}
              value={typeof s.card_fees === "string" ? s.card_fees : JSON.stringify(s.card_fees, null, 1)}
              onChange={(e) => setS({ ...s, card_fees: e.target.value })}
            />
            <p className="text-[11px] text-slate-400 mt-1">As taxas nunca são exibidas ao cliente.</p>
          </div>
        </div>

        <SplashCard />

        <button
          data-testid="save-settings-btn"
          onClick={save}
          disabled={saving}
          className="w-full py-4 rounded-2xl bg-navy text-[#F5B301] font-heading font-extrabold text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
        >
          <Save className="w-4 h-4" /> SALVAR CONFIGURAÇÕES
        </button>
      </div>
    </AdminShell>
  );
}

export default function AdminSettings() {
  return <AdminGate>{(user) => <SettingsForm user={user} />}</AdminGate>;
}
